import { useState, useEffect, useRef } from 'react';
import { getMyShop, updateShop } from '../utils/api';

const SERVICES = [
  { id: 'washing', label: '🫧 Washing' },
  { id: 'dry_cleaning', label: '👔 Dry Cleaning' },
  { id: 'laundry', label: '🧺 Full Laundry' },
  { id: 'ironing', label: '♨️ Ironing' },
];

function MapPicker({ onSelect, initialLat = 12.9716, initialLng = 77.5946 }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (mapInstanceRef.current) return;
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (window.L) { initMap(); return; }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => initMap();
    document.head.appendChild(script);
  }, []);

  const initMap = () => {
    const L = window.L;
    const map = L.map(mapRef.current).setView([initialLat, initialLng], 14);
    mapInstanceRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    const marker = L.marker([initialLat, initialLng], { draggable: true }).addTo(map);
    onSelect({ lat: initialLat, lng: initialLng });
    marker.on('dragend', (e) => { const { lat, lng } = e.target.getLatLng(); onSelect({ lat, lng }); });
    map.on('click', (e) => { const { lat, lng } = e.latlng; marker.setLatLng([lat, lng]); onSelect({ lat, lng }); });
  };

  return (
    <div>
      <div ref={mapRef} style={{ height: 240, borderRadius: 12, border: '1.5px solid var(--border)', overflow: 'hidden' }} />
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 6 }}>Click map or drag pin to update location</p>
    </div>
  );
}

export default function OwnerShopPage() {
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const [form, setForm] = useState({
    shop_name: '', owner_name: '', address: '', pincode: '',
    description: '', phone: '', services: [],
    location: { type: 'Point', coordinates: [0, 0] }
  });

  useEffect(() => { loadShop(); }, []);

  const loadShop = async () => {
    try {
      const res = await getMyShop();
      const s = res.data;
      setShop(s);
      setForm({
        shop_name: s.name || s.shop_name || '',
        owner_name: s.owner_name || '',
        address: s.address || '',
        pincode: s.pincode || '',
        description: s.description || '',
        phone: s.phone || '',
        services: s.services || [],
        location: s.location || { type: 'Point', coordinates: [0, 0] },
      });
    } catch (e) {}
    finally { setLoading(false); }
  };

  const detectGPS = () => {
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({ ...f, location: { type: 'Point', coordinates: [pos.coords.longitude, pos.coords.latitude] } }));
        setDetecting(false);
      },
      () => { setError('Location access denied'); setDetecting(false); }
    );
  };

  const toggleService = (id) =>
    setForm(f => ({ ...f, services: f.services.includes(id) ? f.services.filter(s => s !== id) : [...f.services, id] }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.services.length) return setError('Select at least one service');
    setSaving(true); setError('');
    try {
      const res = await updateShop({
        shop_name: form.shop_name, owner_name: form.owner_name,
        address: form.address, pincode: form.pincode,
        description: form.description, phone: form.phone,
        services: form.services, location: form.location
      });
      setShop(res.data);
      setEditing(false);
      setSuccess('Shop updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const hasLocation = form.location?.coordinates?.[0] !== 0;

  if (loading) return <div className="page"><div className="spinner" /></div>;

  return (
    <div className="page" style={{ maxWidth: 700 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1>My Shop</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>Manage your laundry shop profile</p>
        </div>
        {!editing && <button className="btn btn-outline" onClick={() => setEditing(true)}>✏️ Edit Shop</button>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✅ {success}</div>}

      {/* View mode */}
      {shop && !editing && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
            {[
              { val: shop.avg_rating > 0 ? shop.avg_rating.toFixed(1) : '—', label: 'Avg Rating', color: '#fef3c7', text: '#92400e', icon: '⭐' },
              { val: shop.total_ratings || 0, label: 'Total Reviews', color: 'var(--primary-light)', text: 'var(--primary-dark)', icon: '💬' },
              { val: shop.is_active ? 'Active' : 'Paused', label: 'Status', color: shop.is_active ? '#d1fae5' : '#fee2e2', text: shop.is_active ? '#065f46' : '#991b1b', icon: shop.is_active ? '🟢' : '🔴' },
            ].map(s => (
              <div key={s.label} style={{ padding: '14px', background: s.color, borderRadius: 12, textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: 2 }}>{s.icon}</div>
                <div style={{ fontFamily: 'poppins, sans-serif', fontSize: '1.3rem', fontWeight: 800, color: s.text }}>{s.val}</div>
                <div style={{ fontSize: '0.75rem', color: s.text, opacity: 0.75 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div className="card">
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', background: 'linear-gradient(135deg, var(--primary-light), white)', borderRadius: '16px 16px 0 0' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 56, height: 56, background: 'white', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', boxShadow: 'var(--shadow)', flexShrink: 0 }}>🏪</div>
                <div>
                  <h2 style={{ fontSize: '1.3rem' }}>{shop.name || shop.shop_name}</h2>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 2 }}>Owner: {shop.owner_name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>📞 {shop.phone}</div>
                </div>
              </div>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: 14 }}>
                <div className="form-label">Address</div>
                <p>📍 {shop.address}{shop.pincode ? ` — ${shop.pincode}` : ''}</p>
              </div>
              {shop.description && (
                <div style={{ marginBottom: 14 }}>
                  <div className="form-label">About</div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{shop.description}</p>
                </div>
              )}
              <div>
                <div className="form-label">Services</div>
                <div className="service-options">
                  {shop.services?.map(s => (
                    <div key={s} className="service-chip selected" style={{ cursor: 'default' }}>
                      {SERVICES.find(sv => sv.id === s)?.label || s}
                    </div>
                  ))}
                </div>
              </div>
              {shop.location?.coordinates?.[0] !== 0 && (
                <div style={{ marginTop: 14, padding: '8px 12px', background: '#d1fae5', borderRadius: 8, fontSize: '0.82rem', color: '#065f46' }}>
                  📍 Map location: {shop.location.coordinates[1]?.toFixed(4)}, {shop.location.coordinates[0]?.toFixed(4)}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Edit form */}
      {editing && (
        <form onSubmit={handleSave}>
          <div className="card">
            <div className="card-header"><h3>Edit Shop Details</h3></div>
            <div className="card-body">
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Shop Name *</label>
                  <input className="form-control" value={form.shop_name}
                    onChange={e => setForm(f => ({ ...f, shop_name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Owner Name *</label>
                  <input className="form-control" value={form.owner_name}
                    onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Phone *</label>
                <input className="form-control" value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Address *</label>
                <input className="form-control" value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input className="form-control" placeholder="e.g. 560001" maxLength={6}
                  value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value.replace(/\D/g, '') }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={3} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Services *</label>
                <div className="service-options">
                  {SERVICES.map(s => (
                    <div key={s.id} className={`service-chip ${form.services.includes(s.id) ? 'selected' : ''}`}
                      onClick={() => toggleService(s.id)}>{s.label}</div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Shop Location</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <button type="button" className={`btn btn-sm ${showMap ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setShowMap(s => !s)}>🗺 Pick on Map</button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={detectGPS} disabled={detecting}>
                    {detecting ? '📡 Detecting...' : '📍 Use GPS'}
                  </button>
                </div>
                {showMap && (
                  <div style={{ marginBottom: 10 }}>
                    <MapPicker
                      initialLat={form.location?.coordinates[1] || 12.9716}
                      initialLng={form.location?.coordinates[0] || 77.5946}
                      onSelect={({ lat, lng }) => setForm(f => ({ ...f, location: { type: 'Point', coordinates: [lng, lat] } }))}
                    />
                  </div>
                )}
                {hasLocation && (
                  <div style={{ padding: '6px 10px', background: '#d1fae5', borderRadius: 8, fontSize: '0.78rem', color: '#065f46' }}>
                    ✅ {form.location.coordinates[1].toFixed(4)}, {form.location.coordinates[0].toFixed(4)}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setEditing(false); setError(''); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
